/**
 * LinkedIn admin — agency-level App credentials.
 *
 * Mirrors `MetaIntegrationsPage.js`: this page only holds the
 * agency-wide LinkedIn App credentials (Client ID / Client Secret /
 * Redirect URI) plus a per-client advisory banner. The actual per-
 * client OAuth flow (Ad Accounts, Company Pages, Community) will
 * consume these credentials once the per-client Integrations hub
 * ships.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import {
  Linkedin, Save, ExternalLink, Eye, EyeOff, Users, CheckCircle2,
} from "lucide-react";

export default function LinkedInIntegrationsPage() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  const [clientIdVal, setClientIdVal] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [redirectUriVal, setRedirectUriVal] = useState("");
  // Second app — Community Management API
  const [pagesClientIdVal, setPagesClientIdVal] = useState("");
  const [pagesClientSecret, setPagesClientSecret] = useState("");
  const [showPagesSecret, setShowPagesSecret] = useState(false);
  const [busyPages, setBusyPages] = useState(false);

  const defaultRedirect = `${window.location.origin}/integrations/linkedin/callback`;

  const refresh = async () => {
    try {
      const { data } = await api.get("/integrations/linkedin/settings");
      setSettings(data);
      setClientIdVal(data.client_id || "");
      setRedirectUriVal(data.redirect_uri || defaultRedirect);
      setPagesClientIdVal(data.pages_client_id || "");
    } catch (e) {
      logger.warn("LinkedInIntegrations: load settings failed", e);
      setSettings({ products: [], default_scopes: [] });
      setRedirectUriVal(defaultRedirect);
    }
  };

  useEffect(() => { refresh(); }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const payload = {
        client_id: clientIdVal.trim(),
        redirect_uri: (redirectUriVal || defaultRedirect).trim(),
      };
      if (clientSecret.trim()) payload.client_secret = clientSecret.trim();
      await api.put("/integrations/linkedin/settings", payload);
      toast.success("Ads app credentials saved");
      setClientSecret("");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const savePages = async (e) => {
    e?.preventDefault?.();
    setBusyPages(true);
    try {
      const payload = { pages_client_id: pagesClientIdVal.trim() };
      if (pagesClientSecret.trim()) payload.pages_client_secret = pagesClientSecret.trim();
      await api.put("/integrations/linkedin/settings", payload);
      toast.success("Pages app credentials saved");
      setPagesClientSecret("");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusyPages(false); }
  };

  if (!settings) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  const products = settings.products || [];

  return (
    <div className="space-y-8 max-w-4xl" data-testid="linkedin-admin-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-[#0A66C2] text-white">
          <Linkedin className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="label-overline">Integrations</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">LinkedIn admin</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Agency-level LinkedIn App credentials. Save the Client ID,
            Client Secret and Redirect URI once — every per-client
            LinkedIn OAuth round will reuse these to authorise that
            client&apos;s Company Page, Ad Account and Community assets.
          </p>
        </div>
      </div>

      {/* Per-client info banner */}
      <div className="rounded-sm border border-[#0A66C2]/25 bg-[#EAF3FB] p-4 flex items-start gap-3"
        data-testid="linkedin-per-client-banner">
        <Users className="h-5 w-5 text-[#0A66C2] mt-0.5 shrink-0" />
        <div className="text-sm text-[#0A3A73]">
          <div className="font-semibold">LinkedIn connections live per-client</div>
          <div className="text-[12px] mt-1 leading-relaxed">
            Once these credentials are saved, each client connects their
            own LinkedIn from <span className="font-mono">Clients → pick a client → LinkedIn</span>.
            One OAuth grant per client scopes their Company Page + Ad
            Account exclusively to that client — no master discovery, no
            shared tokens. This page only holds the agency-wide App
            credentials.
          </div>
        </div>
      </div>

      {/* App credentials card — App 1 (Advertising + OIDC + Share) */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="label-overline">App 1 · Advertising + OIDC + Share</div>
              <div className="font-display font-bold text-xl">Ads app credentials</div>
              <p className="text-xs text-zinc-500 mt-1">
                Owns the Advertising API, OpenID Connect sign-in and
                Share on LinkedIn products. Used by every client&apos;s
                Ad-account + paid-analytics sync.
              </p>
            </div>
            <Badge className={`rounded-sm ${settings.configured
              ? "bg-emerald-100 text-emerald-800 border-emerald-200"
              : "bg-amber-100 text-amber-800 border-amber-200"}`}
              data-testid="linkedin-master-config-badge">
              {settings.configured ? "Configured" : "Secret required"}
            </Badge>
          </div>
          <form onSubmit={save} className="grid md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-overline">LinkedIn Client ID</Label>
              <Input value={clientIdVal} onChange={(e) => setClientIdVal(e.target.value)}
                className="rounded-sm font-mono text-xs h-10"
                placeholder="86abc1defghij2"
                data-testid="linkedin-client-id-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">
                LinkedIn Client Secret {settings.has_client_secret && (
                  <span className="ml-2 normal-case tracking-normal text-emerald-700 text-[10px]">· stored</span>
                )}
              </Label>
              <div className="flex gap-2">
                <Input type={showSecret ? "text" : "password"}
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder={settings.has_client_secret ? "•••••••• (leave blank to keep)" : "Paste secret"}
                  className="rounded-sm font-mono text-xs h-10 flex-1"
                  data-testid="linkedin-client-secret-input" />
                <Button type="button" variant="outline" className="rounded-sm" size="sm"
                  onClick={() => setShowSecret((s) => !s)}
                  data-testid="linkedin-toggle-secret">
                  {showSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>

            <div className="md:col-span-2 space-y-1.5">
              <Label className="label-overline">Redirect URI (paste into LinkedIn App)</Label>
              <Input value={redirectUriVal}
                onChange={(e) => setRedirectUriVal(e.target.value)}
                className="rounded-sm font-mono text-[11px] h-9"
                data-testid="linkedin-redirect-uri" />
              <p className="text-[11px] text-zinc-500">
                Add this URI under{" "}
                <span className="font-mono">Auth → OAuth 2.0 settings → Authorized redirect URLs for your app</span>.
                Default:{" "}
                <button type="button" onClick={() => setRedirectUriVal(defaultRedirect)}
                  className="underline font-mono text-[10px]"
                  data-testid="linkedin-redirect-uri-default">
                  {defaultRedirect}
                </button>
              </p>
            </div>

            <div className="md:col-span-2 flex justify-end">
              <Button type="submit" disabled={busy}
                className="rounded-sm bg-[#0A66C2] hover:bg-[#084d94] text-white"
                data-testid="linkedin-save-settings">
                <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save credentials"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* App credentials card — App 2 (Community Management API) */}
      <Card className="rounded-sm border-zinc-200" data-testid="linkedin-pages-card">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="label-overline">App 2 · Community Management</div>
              <div className="font-display font-bold text-xl">Pages app credentials</div>
              <p className="text-xs text-zinc-500 mt-1">
                LinkedIn requires the Community Management API to live on
                a separate developer app (it can&apos;t co-exist with
                Advertising API). Owns organic Company Page reading,
                posting and follower analytics. Same redirect URI as
                above — LinkedIn accepts it on both apps.
              </p>
            </div>
            <Badge className={`rounded-sm ${settings.pages_configured
              ? "bg-emerald-100 text-emerald-800 border-emerald-200"
              : "bg-amber-100 text-amber-800 border-amber-200"}`}
              data-testid="linkedin-pages-config-badge">
              {settings.pages_configured ? "Configured" : "Secret required"}
            </Badge>
          </div>
          <form onSubmit={savePages} className="grid md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-overline">Pages app · Client ID</Label>
              <Input value={pagesClientIdVal}
                onChange={(e) => setPagesClientIdVal(e.target.value)}
                className="rounded-sm font-mono text-xs h-10"
                placeholder="866er3nj3pvlcy"
                data-testid="linkedin-pages-client-id-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">
                Pages app · Client Secret {settings.has_pages_client_secret && (
                  <span className="ml-2 normal-case tracking-normal text-emerald-700 text-[10px]">· stored</span>
                )}
              </Label>
              <div className="flex gap-2">
                <Input type={showPagesSecret ? "text" : "password"}
                  value={pagesClientSecret}
                  onChange={(e) => setPagesClientSecret(e.target.value)}
                  placeholder={settings.has_pages_client_secret ? "•••••••• (leave blank to keep)" : "Paste secret"}
                  className="rounded-sm font-mono text-xs h-10 flex-1"
                  data-testid="linkedin-pages-client-secret-input" />
                <Button type="button" variant="outline" className="rounded-sm" size="sm"
                  onClick={() => setShowPagesSecret((s) => !s)}
                  data-testid="linkedin-pages-toggle-secret">
                  {showPagesSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>
            <div className="md:col-span-2 flex justify-end">
              <Button type="submit" disabled={busyPages}
                className="rounded-sm bg-[#0A66C2] hover:bg-[#084d94] text-white"
                data-testid="linkedin-pages-save">
                <Save className="h-4 w-4 mr-2" />
                {busyPages ? "Saving…" : "Save Pages credentials"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Products / scopes reference */}
      <Card className="rounded-sm border-zinc-200" data-testid="linkedin-products-card">
        <CardContent className="p-6 space-y-4">
          <div>
            <div className="font-display font-bold text-xl">Registered products &amp; scopes</div>
            <p className="text-xs text-zinc-500 mt-1">
              These are the scopes each per-client OAuth round will
              request, based on the products enabled on your LinkedIn App.
              Marketing Developer Platform + Community Management API
              require LinkedIn approval before ad-account scopes will
              unlock for production users.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {products.map((p) => (
              <div key={p.key}
                className="border border-zinc-200 rounded-sm p-3 space-y-1.5"
                data-testid={`linkedin-product-${p.key}`}>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                  <div className="font-semibold text-sm">{p.label}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(p.scopes || []).map((s) => (
                    <span key={s}
                      className="text-[10px] font-mono px-1.5 py-0.5 bg-zinc-100 text-zinc-700 rounded-sm border border-zinc-200">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Per-client login advisory */}
      <Card className="rounded-sm border-zinc-200" data-testid="linkedin-perclient-advice">
        <CardContent className="p-6 space-y-3">
          <div className="font-display font-bold text-xl">Per-client login — how it will work</div>
          <ol className="list-decimal ml-5 space-y-2 text-sm text-zinc-700">
            <li>Open <span className="font-mono">Clients → pick a client → LinkedIn</span>.</li>
            <li>Click <span className="font-mono">Connect LinkedIn</span>. LinkedIn shows the
              consent screen listing the scopes above; the client&apos;s admin approves it once.</li>
            <li>The access token + refresh token are stored encrypted against
              that client only — never mixed with other clients. Ad Account,
              Company Page, and Community assets appear on that client&apos;s
              dashboard automatically.</li>
            <li>If a client already has multiple Company Pages / Ad Accounts,
              they will be listed after consent and can be tagged to the
              client&apos;s brand or business unit.</li>
            <li>Refresh tokens auto-rotate. If a token expires, the client is
              prompted to reconnect from the same LinkedIn tab.</li>
          </ol>
          <p className="text-[12px] text-zinc-500 leading-relaxed pt-2 border-t border-zinc-100">
            <b>Note:</b> The per-client LinkedIn connect UI is not shipped
            yet. Saving credentials here unblocks that build — the OAuth
            round already knows which App to use, which scopes to request,
            and where to send the browser back to.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
